import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import AuroraLogo from "./AuroraLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleEarlyAccessClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (location.pathname === "/") {
      // Already on homepage, just scroll
      const element = document.getElementById("waitlist");
      element?.scrollIntoView({ behavior: "smooth" });
    } else {
      // Navigate to homepage, then scroll after navigation
      navigate("/");
      // Use setTimeout to allow navigation to complete
      setTimeout(() => {
        const element = document.getElementById("waitlist");
        element?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const menuItems = [
    { href: "/#why-aurora", label: "AurØra Approach" },
    { href: "/#how-it-works", label: "How It Works" },
    { href: "/assets", label: "Assets" },
    { href: "/investment-philosophy", label: "Investment Philosophy" },
    { href: "/structure-discipline", label: "Structure and Discipline" },
    { href: "/token-economics", label: "Token Economics" },
    { href: "/risk-disclosure", label: "Risk Disclosure" },
    { href: "/esg", label: "ESG Policy" },
    { href: "/complaints-handling", label: "Complaints Handling" },
    { href: "/legal", label: "Legal" },
    { href: "/contact", label: "Contact" },
  ];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMobileMenuOpen]);

  const MenuLink = ({ href, label, onClick }: { href: string; label: string; onClick?: () => void }) => {
    const isAnchor = href.startsWith("/") && !href.includes("#");
    const linkClass = "block text-foreground hover:text-primary hover:bg-muted/50 hover:translate-x-[-2px] transition-all duration-200 text-sm font-normal py-1.5 px-3 rounded-md text-right font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset";
    
    if (isAnchor) {
      return (
        <Link to={href} className={linkClass} onClick={onClick}>
          {label}
        </Link>
      );
    }
    
    return (
      <a href={href} className={linkClass} onClick={onClick}>
        {label}
      </a>
    );
  };

  const MenuContent = ({ onItemClick }: { onItemClick?: () => void }) => (
    <div className="flex flex-col font-mono">
      {menuItems.map((item) => (
        <MenuLink
          key={item.href}
          href={item.href}
          label={item.label}
          onClick={onItemClick}
        />
      ))}
      <div className="mt-3 pt-2 border-t border-border/40">
        <Link to="/auth" className="w-full" onClick={onItemClick}>
          <Button className="w-full justify-center bg-primary hover:bg-primary/90 text-background rounded-full font-medium font-mono shadow-[0_0_40px_rgba(117,254,184,0.4),0_0_80px_rgba(117,254,184,0.2)] hover:shadow-[0_0_50px_rgba(117,254,184,0.5),0_0_100px_rgba(117,254,184,0.3)] transition-all duration-300">
            Sign In
          </Button>
        </Link>
      </div>
    </div>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50" role="navigation" aria-label="Main navigation">
      <div className="w-full px-3 md:px-5 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md" aria-label="AurØra home">
            <AuroraLogo size={24} />
            <span className="font-sans font-medium text-base text-foreground group-hover:text-primary transition-colors">
              AurØra
            </span>
          </Link>

          {/* Right side: CTA + Burger Menu */}
          <div className="flex items-center gap-3 justify-end">
            <button onClick={handleEarlyAccessClick}>
              <Button variant="outline" size="sm" className="rounded-md border-foreground/20 hover:border-foreground/40 text-xs px-2.5 py-1 h-8 md:text-sm md:px-3 md:h-9">
                <span className="md:hidden">Early Access</span>
                <span className="hidden md:inline">Get Early Access</span>
              </Button>
            </button>
            
            {/* Desktop Dropdown Menu - hidden on mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="hidden md:flex">
                <button
                  className="text-foreground p-2 hover:bg-muted rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Open navigation menu"
                  aria-haspopup="menu"
                >
                  <Menu size={24} aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                side="bottom" 
                sideOffset={8}
                className="w-[320px] max-w-[90vw] rounded-xl border bg-background/95 backdrop-blur p-3 shadow-lg"
              >
                <MenuContent />
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button - visible only on mobile */}
            <div className="md:hidden relative" ref={mobileMenuRef}>
              {/* Backdrop overlay */}
              <div 
                className={`fixed inset-0 bg-background/60 backdrop-blur-sm transition-opacity duration-300 -z-10 ${
                  isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                style={{ top: '56px' }}
                aria-hidden="true"
              />
              
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-foreground p-2 hover:bg-muted rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={isMobileMenuOpen}
                aria-haspopup="dialog"
              >
                <div className="relative w-6 h-6">
                  <Menu 
                    size={24} 
                    aria-hidden="true" 
                    className={`absolute inset-0 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 rotate-90 scale-75' : 'opacity-100 rotate-0 scale-100'}`}
                  />
                  <X 
                    size={24} 
                    aria-hidden="true" 
                    className={`absolute inset-0 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-75'}`}
                  />
                </div>
              </button>

              {/* Mobile Dropdown Panel */}
              <div 
                className={`absolute top-full right-0 mt-2 w-[280px] rounded-2xl border border-border/50 bg-background/95 backdrop-blur-xl p-4 shadow-2xl shadow-primary/5 transition-all duration-300 origin-top-right ${
                  isMobileMenuOpen 
                    ? 'opacity-100 scale-100 translate-y-0' 
                    : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                }`}
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
              >
                {/* Subtle glow effect */}
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-primary/20 via-transparent to-transparent opacity-50 pointer-events-none" />
                
                <div className="relative max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                  <MenuContent onItemClick={() => setIsMobileMenuOpen(false)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
